<template>
  <CurrentEventDataProvider v-slot="{ features }">
    <div
      class="lp-modal"
      role="dialog"
      aria-modal="true"
      :aria-label="$t('Views.Stage.Participants.participants')"
    >
      <!-- Header -->
      <div class="lp-modal__header">
        <div class="lp-modal__title-block">
          <h2 class="lp-modal__title">
            {{ $t('Views.Stage.Participants.participants') }}
          </h2>
          <p class="lp-modal__sub">
            {{ totalLabel }}
          </p>
        </div>
        <button
          class="lp-modal__close"
          @click="onClose"
          :aria-label="$t('Views.Stage.Participants.close')"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Search -->
      <div class="lp-modal__search-wrap">
        <i class="fas fa-search lp-modal__search-icon" aria-hidden="true"></i>
        <input
          v-model="searchInput"
          type="text"
          class="lp-modal__search"
          :placeholder="
            $t('Views.Stage.Participant.LobbyParticipants.searchPlaceholder')
          "
          :aria-label="$t('Views.Stage.Participants.searchForParticipants')"
          @input="onSearchInput"
        />
      </div>

      <!-- List -->
      <div class="lp-modal__list" @scroll="onScroll">
        <div v-if="isLoading && !visibleUsers.length" class="lp-empty">
          <i
            class="fas fa-circle-notch fa-spin"
            style="font-size: 22px; color: var(--color-primary, #047873)"
          ></i>
        </div>

        <div
          v-for="user in visibleUsers"
          :key="user.UserID"
          class="lp-card"
          :class="{ 'lp-card--expanded': expandedId === user.UserID }"
        >
          <div class="lp-card__row">
            <div class="lp-card__avatar-wrap">
              <Avatar
                :image="user.Picture"
                :circle="true"
                class="lp-card__avatar"
                :alt="`${user.Firstname || ''} ${user.Lastname || ''}`"
              />
              <span
                v-if="resolveOnline(user)"
                class="lp-card__avatar-dot"
                :aria-label="
                  $t('Views.Stage.Participant.LobbyParticipants.online')
                "
              ></span>
            </div>

            <div class="lp-card__info">
              <div class="lp-card__name">
                {{ user.Firstname }} {{ user.Lastname }}
                <span
                  v-if="resolveOnline(user)"
                  class="lp-card__status lp-card__status--online"
                  >{{
                    $t('Views.Stage.Participant.LobbyParticipants.online')
                  }}</span
                >
              </div>
              <div v-if="user.Company" class="lp-card__company">
                {{ user.Company }}
              </div>
              <div v-if="user.Jobtitle" class="lp-card__role">
                {{ user.Jobtitle }}
              </div>
            </div>

            <!-- Right-side actions: quick DM, then the "View details"
                 toggle (chat icon first). -->
            <div class="lp-card__right">
              <!-- Quick DM icon — visible only when 1-on-1 chat is
                   enabled for the current platform context. -->
              <button
                v-if="isChatVisible(features) && !isCurrentUser(user)"
                class="lp-card__dm-btn"
                @click="openChat(user)"
                :aria-label="
                  $t(
                    'Views.Stage.Participant.LobbyParticipants.sendMessageTo',
                    {
                      name: `${user.Firstname || ''} ${user.Lastname || ''}`
                    }
                  )
                "
                :title="
                  $t('Views.Stage.Participant.LobbyParticipants.messageUser', {
                    name: user.Firstname || ''
                  })
                "
              >
                <i class="far fa-comment-dots"></i>
              </button>

              <button
                v-if="userHasMoreInfo(user)"
                class="lp-moreinfo"
                :class="{ 'lp-moreinfo--open': expandedId === user.UserID }"
                :aria-expanded="expandedId === user.UserID ? 'true' : 'false'"
                @click="toggleMore(user)"
              >
                <span>{{
                  expandedId === user.UserID
                    ? $t(
                        'Views.Stage.Participant.LobbyParticipants.hideDetails'
                      )
                    : $t(
                        'Views.Stage.Participant.LobbyParticipants.viewDetails'
                      )
                }}</span>
                <i
                  class="fas fa-chevron-down lp-moreinfo__chevron"
                  aria-hidden="true"
                ></i>
              </button>
            </div>
          </div>

          <!-- Expanded info -->
          <div v-if="expandedId === user.UserID" class="lp-card__expanded">
            <div v-if="participantEmailVisible(user)" class="lp-detail-row">
              <div class="lp-detail-row__icon lp-detail-row__icon--teal">
                <i class="far fa-envelope"></i>
              </div>
              <a
                :href="'mailto:' + participantEmail(user)"
                class="lp-detail-row__link"
                >{{ participantEmail(user) }}</a
              >
            </div>
            <div v-if="participantPhoneVisible(user)" class="lp-detail-row">
              <div class="lp-detail-row__icon lp-detail-row__icon--green">
                <i class="fas fa-phone"></i>
              </div>
              <a
                :href="'tel:' + participantPhone(user)"
                class="lp-detail-row__link"
                >{{ participantPhone(user) }}</a
              >
            </div>
            <div
              v-if="participantContactVisible(user) && user.About"
              class="lp-detail-row lp-detail-row--about"
            >
              <div
                class="lp-detail-row__about html-content"
                v-html="user.About"
              ></div>
            </div>
            <div
              v-if="
                !participantEmailVisible(user) &&
                !participantPhoneVisible(user) &&
                !(participantContactVisible(user) && user.About)
              "
              class="lp-detail-empty"
            >
              {{
                $t(
                  'Views.Stage.Participant.LobbyParticipants.noAdditionalDetails'
                )
              }}
            </div>
          </div>
        </div>

        <div v-if="!isLoading && !visibleUsers.length" class="lp-empty">
          <i
            class="far fa-user"
            style="font-size: 30px; color: #d1d5db; margin-bottom: 8px"
          ></i>
          <p>{{ $t('Views.Stage.Participant.LobbyParticipants.noResults') }}</p>
        </div>

        <div v-if="isLoading && users.length" class="lp-empty lp-empty--inline">
          <i
            class="fas fa-circle-notch fa-spin"
            style="font-size: 16px; color: var(--color-primary, #047873)"
          ></i>
        </div>
      </div>
    </div>
  </CurrentEventDataProvider>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Avatar from '@/components/shared/Avatar';
import focusTrapMixin from '@/mixins/focusTrap';
import participantVisibility from '@/mixins/participantVisibility';

export default {
  name: 'LobbyParticipants',
  components: { Avatar },
  mixins: [focusTrapMixin, participantVisibility],
  data() {
    return {
      searchInput: '',
      debouncedSearch: '',
      page: 1,
      pageSize: 20,
      searchTimeout: null,
      expandedId: null
    };
  },
  computed: {
    ...mapGetters('Users', {
      users: 'getUsers',
      isLoading: 'isLoading',
      isUserOnline: 'isUserOnline'
    }),
    ...mapGetters('Authentication', { currentProfile: 'getUser' }),
    ...mapGetters('CurrentEvent', ['participantVisibilityMode']),
    onlineUserIds() {
      return (
        (this.$store.state.Users && this.$store.state.Users.onlineUserIds) || []
      );
    },
    // Visible vs Limited-visible vs Not-visible — applied to the
    // participant list. In 'limited' mode the participants ARE still
    // shown (the only difference is that the "More info" button is
    // suppressed so contact / about-me details stay private). The
    // list is fully hidden only in 'hidden' mode (modal auto-closes).
    visibleUsers() {
      return Array.isArray(this.users) ? this.users : [];
    },
    totalLabel() {
      const len = this.visibleUsers.length;
      if (!len)
        return this.$t(
          'Views.Stage.Participant.LobbyParticipants.noParticipantsLoaded'
        );
      return this.$tc(
        'Views.Stage.Participant.LobbyParticipants.participantCount',
        len,
        {
          count: `${len}${len < this.pageSize ? '' : '+'}`
        }
      );
    }
  },
  watch: {
    // Auto-close if admin switches the event to "Not-visible" while
    // the modal is open.
    participantVisibilityMode: {
      immediate: true,
      handler(mode) {
        if (mode === 'hidden') this.onClose();
      }
    }
  },
  async created() {
    document.body.classList.add('overflow-hidden');
    await this.fetchPage(true);
    this.activateFocusTrap();
  },
  beforeDestroy() {
    this.deactivateFocusTrap();
    document.body.classList.remove('overflow-hidden');
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
  },
  methods: {
    ...mapActions('Users', ['fetchUsers']),
    onSearchInput() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.debouncedSearch = (this.searchInput || '').trim();
        this.page = 1;
        this.fetchPage(true);
      }, 350);
    },
    async fetchPage(replace = false) {
      try {
        await this.fetchUsers({
          page: this.page,
          pageSize: this.pageSize,
          search: this.debouncedSearch,
          groupId: '',
          append: !replace
        });
      } catch (e) {
        // ignore
      }
    },
    async onScroll(e) {
      const el = e.target;
      if (!el || this.isLoading) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 60) {
        this.page += 1;
        await this.fetchPage(false);
      }
    },
    resolveOnline(user) {
      if (!user || !user.UserID) return false;
      // Prefer the realtime onlineUserIds list, but fall back to the
      // Online flag the API returns on the user object so we don't show
      // a stale offline state when pusher hasn't started pushing yet.
      if (this.onlineUserIds && this.onlineUserIds.includes(user.UserID))
        return true;
      return user.Online === true || user.Online === 1 || user.Online === '1';
    },
    // Whether THIS participant has any admin-permitted detail to reveal.
    // Honours the visibility mode + per-participant opt-in via the shared
    // participantVisibility mixin, so Limited-visible mode correctly
    // shows details for participants who opted in (instead of hiding the
    // button for everyone).
    userHasMoreInfo(user) {
      return (
        this.participantEmailVisible(user) ||
        this.participantPhoneVisible(user) ||
        (this.participantContactVisible(user) && !!user.About)
      );
    },
    isChatVisible(features) {
      const route = this.$route.name;
      if (!features) return false;
      // Each route honours only its own chat toggle now — turning
      // "1-on-1 chat" off for the lobby in admin must hide the chat
      // button on the lobby's View All Attendees modal even if the
      // stage chat toggle happens to be on.
      if (route === 'lobby') return !!features.lobbyChatEnabled;
      if (route === 'live-stream') return !!features.stageChatEnabled;
      if (route === 'stage') return !!features.stageChatEnabled;
      if (route === 'webapp') return !!features.webAppChat;
      return !!(
        features.lobbyChatEnabled ||
        features.stageChatEnabled ||
        features.webAppChat
      );
    },
    isCurrentUser(user) {
      return (
        this.currentProfile &&
        user &&
        user.UserID === this.currentProfile.UserID
      );
    },
    toggleMore(user) {
      this.expandedId = this.expandedId === user.UserID ? null : user.UserID;
    },
    openChat(user) {
      // Close this modal then either navigate to the dedicated chat
      // page (on mobile) or open the themed PrivateChatModal scoped to
      // the selected participant (on desktop).
      const isMobile =
        typeof window !== 'undefined' && window.innerWidth <= 768;
      this.onClose();
      this.$nextTick(() => {
        if (isMobile) {
          const userId = user && user.UserID;
          this.$router
            .push({
              name: 'private-chat',
              params: userId ? { userId: String(userId) } : {}
            })
            .catch(() => {
              /* duplicate-nav errors are harmless */
            });
          return;
        }
        // Desktop: dock a LinkedIn-style chat window at the bottom-right.
        this.$store.dispatch('Chat/openChatWindow', user);
      });
    },
    onClose() {
      document.body.classList.remove('overflow-hidden');
      this.$emit('close');
    }
  }
};
</script>

<style scoped lang="scss">
.lp-modal {
  background: #ffffff;
  border-radius: 16px;
  font-family: 'Poppins', sans-serif;
  display: flex;
  flex-direction: column;
  max-height: 85vh;
  overflow: hidden;
}

.lp-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 22px 24px 16px;
  border-bottom: 1px solid #f3f4f6;
  flex-shrink: 0;
}

.lp-modal__title-block {
  flex: 1;
  min-width: 0;
}

.lp-modal__title {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin: 0;
  line-height: 1.2;
}

.lp-modal__sub {
  font-size: 12px;
  color: #6b7280;
  margin: 4px 0 0;
}

.lp-modal__close {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: #f3f4f6;
  color: #374151;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  transition: background 0.15s;
  flex-shrink: 0;

  &:hover {
    background: #e5e7eb;
  }
}

.lp-modal__search-wrap {
  position: relative;
  margin: 16px 24px 0;
  flex-shrink: 0;
}

.lp-modal__search-icon {
  position: absolute;
  top: 50%;
  left: 14px;
  transform: translateY(-50%);
  color: #6b7280;
  font-size: 13px;
}

.lp-modal__search {
  width: 100%;
  padding: 11px 14px 11px 38px;
  background: #f3f3f5;
  border: 1.5px solid #b0b8c4;
  border-radius: 10px;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: #111827;
  transition: border-color 0.15s, background 0.15s;

  &::placeholder {
    color: #6b7280;
  }

  &:focus {
    outline: none;
    background: #ffffff;
    border-color: var(--color-primary, #047873);
  }
}

.lp-modal__list {
  flex: 1;
  overflow-y: auto;
  padding: 16px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

// ── Card ─────────────────────────────────────────────────────────────
.lp-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 16px 18px;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    border-color: var(--color-primary, #047873);
    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.06);
  }

  &--expanded {
    border-color: var(--color-primary, #047873);
    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.06);
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 14px;
    // Allow the action group to drop onto its own line on narrow cards
    // (e.g. the modal at mobile widths) instead of crowding the name.
    flex-wrap: wrap;
  }

  &__avatar-wrap {
    position: relative;
    width: 44px;
    height: 44px;
    flex-shrink: 0;
  }

  &__avatar {
    width: 44px !important;
    height: 44px !important;
    border-radius: 50%;
    overflow: hidden;
    background: #e5e7eb;

    ::v-deep img,
    ::v-deep .avatar {
      width: 100% !important;
      height: 100% !important;
      object-fit: cover;
      border-radius: 50%;
    }
  }

  &__avatar-dot {
    position: absolute;
    bottom: 1px;
    right: 1px;
    width: 11px;
    height: 11px;
    border-radius: 50%;
    background: #22c55e;
    border: 2px solid #ffffff;
  }

  &__info {
    // Keep a sensible minimum so the name never collapses to a few
    // pixels behind the action buttons; the row wraps instead.
    flex: 1 1 150px;
    min-width: 0;
  }

  &__name {
    font-size: 14px;
    font-weight: 700;
    color: #111827;
    line-height: 1.3;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__status {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 9999px;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;

    &--online {
      background: #dcfce7;
      color: #15803d;
    }
  }

  &__company {
    font-size: 13px;
    color: #374151;
    margin-top: 2px;
  }

  &__role {
    font-size: 12px;
    color: #6b7280;
    margin-top: 2px;
  }

  &__actions {
    display: flex;
    gap: 8px;
    margin-top: 12px;
    flex-wrap: wrap;
  }

  // Right-side action group — "View details" toggle next to the DM icon.
  &__right {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
    align-self: center;
    // Sits at the far right on wide cards; when the row wraps on a
    // narrow card it drops to its own line, still right-aligned.
    margin-left: auto;
  }

  &__expanded {
    margin-top: 16px;
    padding-top: 14px;
    border-top: 1px solid #f3f4f6;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__dm-btn {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: 1.5px solid #e5e7eb;
    background: #ffffff;
    color: var(--color-primary, #047873);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    flex-shrink: 0;
    align-self: flex-start;
    margin-top: 2px;
    transition: background 0.15s, border-color 0.15s, color 0.15s,
      box-shadow 0.15s;

    &:hover {
      background: var(--color-primary, #047873);
      border-color: var(--color-primary, #047873);
      color: #ffffff;
      box-shadow: 0 4px 6px -1px rgba(4, 120, 115, 0.2);
    }
  }
}

// ── Buttons ──────────────────────────────────────────────────────────
.lp-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  padding: 8px 14px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-family: 'Poppins', sans-serif;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  i {
    font-size: 11px;
  }

  &--primary {
    background: var(--color-primary, #047873);
    color: #ffffff;

    &:hover {
      background: var(--color-primary-hover, #036663);
    }
  }

  &--secondary {
    background: #ffffff;
    color: #374151;
    border: 1.5px solid #e5e7eb;

    &:hover {
      border-color: var(--color-primary, #047873);
      color: var(--color-primary, #047873);
    }
  }
}

// ── "View details" toggle — light pill with a rotating chevron ───────
.lp-moreinfo {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 12px;
  border-radius: 9999px;
  border: 1px solid #cfeae7;
  background: #f2faf9;
  color: var(--color-primary, #047873);
  font-size: 12px;
  font-weight: 600;
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  &__chevron {
    font-size: 9px;
    transition: transform 0.2s ease;
  }

  &:hover {
    background: #e6f5f4;
    border-color: var(--color-primary, #047873);
  }

  &:focus-visible {
    outline: 2px solid var(--color-primary, #047873);
    outline-offset: 2px;
  }

  &--open {
    background: #e6f5f4;
    border-color: var(--color-primary, #047873);

    .lp-moreinfo__chevron {
      transform: rotate(180deg);
    }
  }
}

body[data-theme='dark'] {
  .lp-moreinfo {
    background: var(--t-primary-soft);
    border-color: var(--t-border-strong);
    color: var(--t-primary);

    &:hover,
    &--open {
      background: var(--t-primary-soft);
      border-color: var(--t-primary);
    }
  }
}

// ── Detail rows in expanded ──────────────────────────────────────────
.lp-detail-row {
  display: flex;
  align-items: center;
  gap: 12px;

  &__icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    flex-shrink: 0;

    &--teal {
      background: #e6f5f4;
      color: var(--color-primary, #047873);
    }
    &--green {
      background: #dcfce7;
      color: #15803d;
    }
  }

  &__link {
    font-size: 13px;
    color: var(--color-primary, #047873);
    text-decoration: none;
    word-break: break-word;

    &:hover {
      text-decoration: underline;
    }
  }

  &--about {
    align-items: flex-start;
  }

  &__about {
    font-size: 12px;
    color: #6b7280;
    line-height: 1.55;
  }
}

.lp-detail-empty {
  font-size: 12px;
  color: #6b7280;
  text-align: center;
  padding: 6px 0;
}

::v-deep .lp-detail-row__about.html-content {
  p {
    margin: 0 0 6px;
  }
  a {
    color: var(--color-primary, #047873);
  }
}

// ── Empty / loading ──────────────────────────────────────────────────
.lp-empty {
  text-align: center;
  padding: 36px 18px;
  color: #6b7280;

  p {
    font-size: 13px;
    margin: 0;
  }

  &--inline {
    padding: 14px 0;
  }
}
</style>
